
/**
 * Terms of Use / Privacy Policy consent checkbox.
 *
 * ACCESSIBILITY FIX: the error message previously had no id and the checkbox no
 * aria-describedby or aria-invalid, so a screen reader user could hear "checkbox, invalid
 * entry" at best and never learn why. Now wired with aria-invalid + aria-describedby, and the
 * message is role="alert" so it is announced when it appears.
 *
 * @param {object} props
 * @param {boolean} props.checked
 * @param {string} [props.error]
 * @param {Function} props.onChange   (checked: boolean) => void
 * @param {string} props.termsUrl
 * @param {string} props.privacyUrl
 * @param {React.Ref} [props.inputRef]  so the submit blocker list can focus this control
 */
export default function TermsSection({
  checked,
  error,
  onChange,
  termsUrl,
  privacyUrl,
  inputRef,
}) {
  const errorId = 'agreedToTerms-error';

  return (
    /*
      NO CARD, NO ICON BADGE, NO HEADING. This used to be a gray panel titled "Terms and
      Privacy" with its own circular icon. In the redesign the checkbox sits directly beneath
      the order total in the summary column, where a second heading competes with the total for
      attention and the panel implies a section that is really one control.
    */
    <div className="border-t border-gray-200 pt-4">
      <div className="space-y-3">
        <div className="flex items-start gap-3">
          <input
            ref={inputRef}
            type="checkbox"
            id="agreedToTerms"
            name="agreedToTerms"
            checked={checked}
            onChange={(e) => onChange(e.target.checked)}
            aria-invalid={error ? 'true' : undefined}
            aria-describedby={error ? errorId : undefined}
            className={`mt-1 w-4 h-4 rounded border-2 flex-shrink-0 focus:ring-primary-500 ${
              error
                ? 'border-danger-500 focus:border-danger-500'
                : 'border-gray-300 text-primary-600 focus:border-primary-500'
            }`}
            // NO native `required`. It suppressed the form's submit event entirely: the
            // browser blocked submission and popped its own bubble, so React never ran
            // validation and the blocker summary never appeared. That was invisible while the
            // submit button was disabled (submission could not be attempted with the box
            // unchecked); making the button reachable exposed it.
            //
            // The requirement is conveyed by aria-invalid + the blocker list instead, matching
            // TextField, which omits `required` for the same reason.
          />
          <label htmlFor="agreedToTerms" className="text-sm text-gray-700 leading-relaxed">
            I agree to the{' '}
            <a
              href={termsUrl}
              target="_blank"
              rel="noopener noreferrer"
              // primary-700 rather than primary-600: #ea580c on white is ~3.6:1 and fails
              // WCAG AA for body-size text. primary-700 (#c2410c) is ~5.1:1.
              className="text-primary-700 hover:text-primary-800 underline font-medium"
            >
              Terms of Use
            </a>{' '}
            and{' '}
            <a
              href={privacyUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary-700 hover:text-primary-800 underline font-medium"
            >
              Privacy Policy
            </a>
          </label>
        </div>

        {error && (
          <p id={errorId} role="alert" className="text-sm text-danger-600 ml-7">
            {error}
          </p>
        )}
      </div>
    </div>
  );
}
